import React, { memo } from 'react'
import { ContextFetcher } from './Fetcher'
import {
  ContextFetcherPropsType,
  ContextFetchLoaderPropsType,
  GenericFetchHookParamsType
} from './types'

/**
 * @function castFetchContext - allows you to create a memoized FetchContext that is cast to a particular generic
 * @template <FetchDataType> - the data type handled from this context fetcher
 * @template <FetchHookParamsType> - the parameters that need to be sent to the context fetch hook function
 * @param {ContextFetchLoaderPropsType} loaderProps - optional loader properties to apply to the Fetcher
 *   (to avoid needing to apply the properties in the underlying Loader consumer of the Fetcher component)
 * @return returns a ContextFetcher with the specified <T> generic applied
 */
export function castFetchContext<
  FetchDataType,
  FetchHookParamsType = GenericFetchHookParamsType
>(): React.ComponentType<
  | ContextFetcherPropsType
  | ContextFetchLoaderPropsType<FetchDataType, FetchHookParamsType>
> {
  return memo(
    (
      props:
        | ContextFetcherPropsType
        | ContextFetchLoaderPropsType<FetchDataType, FetchHookParamsType>
    ): JSX.Element =>
      ContextFetcher<FetchDataType, FetchHookParamsType>(
        props as ContextFetchLoaderPropsType<FetchDataType, FetchHookParamsType>
      )
  )
}

/**
 * this function generates a generic fetch context loader
 * @template <FetchDataType> - the data type handled from this context fetcher
 * @template <FetchHookParamsType> - the parameters that need to be sent to the context fetch hook function
 * @params {Object} params - the parameters to send to the context fetcher
 * @return {React.Component} - the fetch context loader component
 */
export function createContextFetchLoader<
  FetchDataType,
  FetchHookParamsType = GenericFetchHookParamsType
>(
  params: ContextFetchLoaderPropsType<FetchDataType, FetchHookParamsType>
): React.ComponentType<ContextFetcherPropsType & FetchHookParamsType> {
  const Fetcher = castFetchContext<FetchDataType, FetchHookParamsType>()
  return ({
    children,
    ...rest
  }: ContextFetcherPropsType & FetchHookParamsType): JSX.Element => (
    <Fetcher {...params} {...rest}>
      {children}
    </Fetcher>
  )
}

export default createContextFetchLoader
